

import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";

const TEMPLATE_FILENAME = "Pipeline Transportation Company of the year_Award2025.xlsx";

const rowMapping = {
  organisationname: 2,

  crude_oil_2024:7,
  lpg_2024:8,
  natural_gas_2024:9,


  crude_oil_throughput_2024:11,
  lpg_throughput_2024:12,
  natural_gas_throughput_2024:13,

  throughput_crude_2024:17,
  throughput_crude_2023:21,
  throughput_lpg_2024:18,
  throughput_lpg_2023:22,
  throughput_cng_2024:19,
  throughput_cng_2023:23,

  throughput_absolute_crude:25,
  throughput_absolute_lpg:26,
  throughput_absolute_cng:27,
  

reduction_op_oil_2024:33,
reduction_op_oil_2023:37,
reduction_op_lpg_2024:34,
reduction_op_lpg_2023:38,
reduction_op_cng_2024:35,
reduction_op_cng_2023:39,

  reduction_energy_crude_2024:44,
  reduction_energy_crude_2023:48,
  reduction_energy_lpg_2024:45,
  reduction_energy_lpg_2023:49,
  reduction_energy_cng_2024:46,
  reduction_energy_cng_2023:50,


  leaks_crude_2024:53,
  leaks_lpg_2024:54,
  leaks_cng_2024:55,

  loss_2024:58,

  downtime_breakdown_2024:60,
  sabotage_2024:62,

  RE_power:67,
  Total_power:68,
  fatal_accident:70,
  hour_worked1:72,

  lost_time:75,
  hour_worked2:76,

incident:79,
hour_worked3:80,

  
};

/** Helpers for Excel columns / range */
const colLetter = (colIndex) => {
  let letters = "";
  while (colIndex >= 0) {
    letters = String.fromCharCode((colIndex % 26) + 65) + letters;
    colIndex = Math.floor(colIndex / 26) - 1;
  }
  return letters;
};

const colIndexFromLetter = (col) => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 65 + 1);
  }
  return index - 1;
};

const expandSheetRange = (sheet, minCol, minRow, maxCol, maxRow) => {
  const start = `${colLetter(minCol)}${minRow}`;
  const end = `${colLetter(maxCol)}${maxRow}`;
  sheet["!ref"] = `${start}:${end}`;
};

const ExcelPipeline = () => {
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exported, setExported] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiClient.get("pipeline/submitted/");
        const results = (resp.data && (resp.data.results ?? resp.data)) || [];
        console.log("API results length:", results.length);
        setApiData(results);
      } catch (err) {
        console.error("API fetch error:", err);
        setError("Failed to fetch API data. See console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && !error && apiData && apiData.length > 0 && !exported) {
      (async () => {
        try {
          await performExport(apiData);
          setExported(true);
        } catch (err) {
          console.error("Auto-export failed:", err);
        }
      })();
    }
  }, [loading, error, apiData, exported]);

  // Helper that tries many field names
  const getVal = (obj, ...keys) => {
    for (const k of keys) {
      if (!k) continue;
      const val = obj[k];
      if (val !== undefined && val !== null) return val;
    }
    return undefined;
  };

  // resolve merges: if (r,c) lies inside a merged range, return top-left of that merge
  const resolveMergeCell = (sheet, r, c) => {
    const merges = sheet["!merges"] || [];
    for (const m of merges) {
      if (r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c) {
        return { r: m.s.r, c: m.s.c };
      }
    }
    return { r, c };
  };

  const performExport = async (dataToWrite) => {
    if (!dataToWrite || dataToWrite.length === 0) {
      throw new Error("No API data to export.");
    }

    const url = encodeURI(`${process.env.PUBLIC_URL || ""}/templates/${TEMPLATE_FILENAME}`);
    console.log("Fetching template:", url);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Template not found (status ${resp.status})`);

    const contentType = resp.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
      const txt = await resp.text();
      console.error("Template fetch returned HTML (first 300 chars):", txt.slice(0, 300));
      throw new Error("Template returned HTML. Check file path/name.");
    }

    const arrayBuffer = await resp.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    console.log("Using sheet:", sheetName, "initial !ref:", sheet["!ref"]);

    // existing bounds
    const existingRef = sheet["!ref"] || "A1:A1";
    const [startRef, endRef] = existingRef.split(":");
    const startColLetter = (startRef || "A1").replace(/[0-9]/g, "");
    const startRow = parseInt((startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;
    const endColLetter = (endRef || startRef || "A1").replace(/[0-9]/g, "");
    const endRow = parseInt((endRef || startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;

    let minCol = colIndexFromLetter(startColLetter || "A");
    let minRow = startRow;
    let maxCol = colIndexFromLetter(endColLetter || "A");
    let maxRow = endRow;

    // Choose start column (zero-based). Change START_COL if you want to begin at G (6) or somewhere else.
    const START_COL = 6; // 6 => column G. Adjust if your template expects a different starting column.

    dataToWrite.forEach((company, idx) => {
      const colIndex = START_COL + idx; // zero-based
      const col = colLetter(colIndex);
      console.log(`\n--- Writing company #${idx + 1} into column ${col} (zero-based ${colIndex}) ---`);
      console.log("Company raw keys:", Object.keys(company).slice(0,50)); // quick peek

      // attempt multiple key names for each field to be resilient to API changes
      const organisationname = getVal(company, "organisation_name", "organisationname", "org_name", "organisation");
     
     
     
     
     
     
     
      const crude_oil_2024 = getVal(company, "throughput_crude_2024");
      const lpg_2024 = getVal(company, "throughput_liquid_2024", "throughput_liquid_2024");
      const natural_gas_2024 = getVal(company, "throughput_gas_2024");
      const crude_oil_throughput_2024 = getVal(company, "actual_throughput_crude_2024", "actual_throughput_crude_2024");
      const lpg_throughput_2024 = getVal(company, "actual_throughput_liquid_2024");
      const natural_gas_throughput_2024 = getVal(company, "actual_throughput_gas_2024");
      const throughput_crude_2024 = getVal(company, "actual_throughput_crude_2024");
      const throughput_crude_2023 = getVal(company, "actual_throughput_crude_2023", "actual_throughput_crude_2023");
      const throughput_lpg_2024 = getVal(company, "actual_throughput_liquid_2024");
      const throughput_lpg_2023 = getVal(company, "actual_throughput_liquid_2023");
      const throughput_cng_2024 = getVal(company, "actual_throughput_cng_2024");
      const throughput_cng_2023 = getVal(company, "actual_throughput_cng_2023");
      const throughput_absolute_crude = getVal(company, "actual_throughput_crude_2024");
      const throughput_absolute_lpg = getVal(company, "actual_throughput_liquid_2024");
      const throughput_absolute_cng = getVal(company, "actual_throughput_cng_2024");
     
      const reduction_op_oil_2024 = getVal(company, "op_cost_crude_2024");
      const reduction_op_oil_2023 = getVal(company, "op_cost_crude_2023");
      const reduction_op_lpg_2024 = getVal(company, "op_cost_liquid_2024");
      const reduction_op_lpg_2023 = getVal(company, "op_cost_liquid_2023");
      const reduction_op_cng_2024 = getVal(company, "op_cost_gas_2024");
      const reduction_op_cng_2023 = getVal(company, "op_cost_gas_2023");
    
      const reduction_energy_crude_2024 = getVal(company, "energy_crude_2024");
      const reduction_energy_crude_2023 = getVal(company, "energy_crude_2023");
      const reduction_energy_lpg_2024 = getVal(company, "energy_crude_2024");
      const reduction_energy_lpg_2023 = getVal(company, "energy_crude_2023", "energy_crude_2023");
      const reduction_energy_cng_2024 = getVal(company, "energy_gas_2024", "energy_gas_2024");
      const reduction_energy_cng_2023 = getVal(company, "energy_gas_2023", "energy_gas_2023");
      const leaks_crude_2024 = getVal(company, "leaks_crude_2024", "leaks_crude_2024");
      const leaks_lpg_2024 = getVal(company, "leaks_liquid_2024", "leaks_liquid_2024");
      const leaks_cng_2024 = getVal(company, "leaks_gas_2024", "leaks_gas_2024");
      const loss_2024 = getVal(company, "loss_leakage_2024", "loss_leakage_2024");
      const downtime_breakdown_2024 = getVal(company, "downtime_breakdown_2024", "downtime_breakdown_2024");
      const sabotage_2024 = getVal(company, "downtime_sabotage_2024", "downtime_sabotage_2024");
      
      
      const RE_power = getVal(company, "power_re_2024");
      const Total_power = getVal(company, "power_total_2024");
      const fatal_accident = getVal(company, "fatalities_2024");
      const hour_worked1 = getVal(company, "manhours_own_2024");
      const lost_time = getVal(company, "injuries_2024");
      const hour_worked2 = getVal(company, "manhours_own_2024");
      const incident = getVal(company, "osha_incidents_2024");
      const hour_worked3 = getVal(company, "manhours_own_2024");

      const writes = [
        [rowMapping.organisationname, organisationname, "organisationname"],

        [rowMapping.crude_oil_2024, crude_oil_2024, "crude_oil_2024"],
        [rowMapping.lpg_2024, lpg_2024, "lpg_2024"],
        [rowMapping.natural_gas_2024, natural_gas_2024, "natural_gas_2024"],
        
        [rowMapping.crude_oil_throughput_2024, crude_oil_throughput_2024, "crude_oil_throughput_2024"],
        [rowMapping.lpg_throughput_2024, lpg_throughput_2024, "lpg_throughput_2024"],
        [rowMapping.natural_gas_throughput_2024, natural_gas_throughput_2024, "natural_gas_throughput_2024"],
        
        [rowMapping.throughput_crude_2024, throughput_crude_2024, "throughput_crude_2024"],
        [rowMapping.throughput_crude_2023, throughput_crude_2023, "throughput_crude_2023"],
        [rowMapping.throughput_lpg_2024, throughput_lpg_2024, "throughput_lpg_2024"],
        [rowMapping.throughput_lpg_2023, throughput_lpg_2023, "throughput_lpg_2023"],
        [rowMapping.throughput_cng_2024, throughput_cng_2024, "throughput_cng_2024"],
        [rowMapping.throughput_cng_2023, throughput_cng_2023, "throughput_cng_2023"],
       
        [rowMapping.throughput_absolute_crude, throughput_absolute_crude, "throughput_absolute_crude"],
        [rowMapping.throughput_absolute_lpg, throughput_absolute_lpg, "throughput_absolute_lpg"],
        [rowMapping.throughput_absolute_cng, throughput_absolute_cng, "throughput_absolute_cng"],



        [rowMapping.reduction_op_oil_2024, reduction_op_oil_2024, "reduction_op_oil_2024"],
        [rowMapping.reduction_op_oil_2023, reduction_op_oil_2023, "reduction_op_oil_2023"],
        [rowMapping.reduction_op_lpg_2024, reduction_op_lpg_2024, "reduction_op_lpg_2024"],
        [rowMapping.reduction_op_lpg_2023, reduction_op_lpg_2023, "reduction_op_lpg_2023"],
        [rowMapping.reduction_op_cng_2024, reduction_op_cng_2024, "reduction_op_cng_2024"],
        [rowMapping.reduction_op_cng_2023, reduction_op_cng_2023, "reduction_op_cng_2023"],
       
        [rowMapping.reduction_energy_crude_2024, reduction_energy_crude_2024, "reduction_energy_crude_2024"],
        [rowMapping.reduction_energy_crude_2023, reduction_energy_crude_2023, "reduction_energy_crude_2023"],
        [rowMapping.reduction_energy_lpg_2024, reduction_energy_lpg_2024, "reduction_energy_lpg_2024"],
        [rowMapping.reduction_energy_lpg_2023, reduction_energy_lpg_2023, "reduction_energy_lpg_2023"],
        [rowMapping.reduction_energy_cng_2024, reduction_energy_cng_2024, "reduction_energy_cng_2024"],
        [rowMapping.reduction_energy_cng_2023, reduction_energy_cng_2023, "reduction_energy_cng_2023"],


        [rowMapping.leaks_crude_2024, leaks_crude_2024, "leaks_crude_2024"],
        [rowMapping.leaks_lpg_2024, leaks_lpg_2024, "leaks_lpg_2024"],
        [rowMapping.leaks_cng_2024, leaks_cng_2024, "leaks_cng_2024"],
       
        [rowMapping.loss_2024, loss_2024, "loss_2024"],
       
        [rowMapping.downtime_breakdown_2024, downtime_breakdown_2024, "downtime_breakdown_2024"],
        [rowMapping.sabotage_2024, sabotage_2024, "sabotage_2024"],
       
        [rowMapping.RE_power, RE_power, "RE_power"],
        [rowMapping.Total_power, Total_power, "Total_power"],
        [rowMapping.fatal_accident, fatal_accident, "fatal_accident"],
        [rowMapping.hour_worked1, hour_worked1, "hour_worked1"],
        [rowMapping.lost_time, lost_time, "lost_time"],
        [rowMapping.hour_worked2, hour_worked2, "hour_worked2"],
        [rowMapping.incident, incident, "incident"],
        [rowMapping.hour_worked3, hour_worked3, "hour_worked3"],
      ];

      writes.forEach(([rowNum, value, fieldName]) => {
        if (rowNum == null) return; // missing mapping
        const rZero = rowNum - 1;
        const cZero = colIndex;

        // resolve merges (if writing inside a merged cell, write to master)
        const { r: rResolved, c: cResolved } = resolveMergeCell(sheet, rZero, cZero);

        // If value is undefined/null -> set to empty string but log it
        let writeVal = value;
        if (writeVal === undefined || writeVal === null || writeVal === "") {
          console.warn(`Company #${idx + 1} field "${fieldName}" missing -> writing empty string to ${colLetter(cResolved)}${rResolved + 1}`);
          writeVal = "";
        }

        // Try to coerce numeric-looking strings to numbers
        if (typeof writeVal === "string") {
          const trimmed = writeVal.trim();
          if (trimmed !== "" && /^-?\d+(\.\d+)?$/.test(trimmed)) {
            writeVal = Number(trimmed);
          }
        }

        // Use sheet_add_aoa with { origin: { r, c } } for robust placement
        try {
          XLSX.utils.sheet_add_aoa(sheet, [[writeVal]], { origin: { r: rResolved, c: cResolved } });
          const cellAddress = `${colLetter(cResolved)}${rResolved + 1}`;
          const newCell = sheet[cellAddress];
          if (newCell) {
            if (typeof writeVal === "number") newCell.t = "n";
            else if (typeof writeVal === "boolean") newCell.t = "b";
            else newCell.t = "s";
          }
          console.log(`WROTE ${fieldName} -> ${colLetter(cResolved)}${rResolved + 1} =`, writeVal);
        } catch (e) {
          console.error("Write failed for", fieldName, "at", rResolved, cResolved, e);
          // fallback: write raw cell object
          const fallbackCellRef = `${colLetter(cZero)}${rowNum}`;
          sheet[fallbackCellRef] = { t: typeof writeVal === "number" ? "n" : "s", v: writeVal };
        }

        if (cZero > maxCol) maxCol = cZero;
        if (rowNum > maxRow) maxRow = rowNum;
      });
    });

    // update sheet range so Excel shows new cells
    expandSheetRange(sheet, minCol, minRow, Math.max(maxCol, minCol), Math.max(maxRow, minRow));
    console.log("Updated sheet !ref:", sheet["!ref"]);

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Modified_${TEMPLATE_FILENAME}`);
    console.log("Excel exported successfully.");
  };

  if (loading) return <div>Loading API data...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  return (
        <>
    {navigate("/Thankyou")}
    </>
  );
};

export default ExcelPipeline;
